'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Content, PageRoot } from './atoms';
import { LeaderboardBackground } from './LeaderboardBackground';
import { LeaderboardHero } from './LeaderboardHero';
import { LeaderboardList } from './LeaderboardList';
import { LeaderboardStickyBar } from './LeaderboardStickyBar';
import type { LeaderboardPageData } from './types';
import type { Gender } from './types';
import { useLeaderboardStickyTop } from './useLeaderboardStickyTop';
import styles from './LeaderboardPage.module.css';

type LeaderboardPageProps = {
  data: LeaderboardPageData;
};

export const LeaderboardPage: React.FC<LeaderboardPageProps> = ({ data }) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [gender, setGender] = useState<Gender>('men');
  const [isCompact, setIsCompact] = useState(false);
  const stickyTop = useLeaderboardStickyTop();

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    let observer: IntersectionObserver | null = null;

    const attachObserver = () => {
      observer?.disconnect();
      observer = new IntersectionObserver(
        ([entry]) => {
          setIsCompact(!entry?.isIntersecting);
        },
        {
          root: null,
          rootMargin: `-${stickyTop}px 0px 0px 0px`,
          threshold: 0,
        },
      );
      observer.observe(sentinel);
    };

    attachObserver();
    window.addEventListener('resize', attachObserver);
    return () => {
      window.removeEventListener('resize', attachObserver);
      observer?.disconnect();
    };
  }, [stickyTop]);

  const entries = data.byGender[gender];

  return (
    <PageRoot ref={pageRef}>
      <LeaderboardBackground />
      <LeaderboardStickyBar
        visible={isCompact}
        headerOffset={stickyTop}
        gender={gender}
        onGenderChange={setGender}
      />
      <Content>
        <LeaderboardHero />
        <div className={styles.heroSentinel} ref={sentinelRef} aria-hidden="true" />
        <div className={styles.listWrap}>
          <LeaderboardList
            entries={entries}
            ageByCategory={data.ageByCategoryByGender[gender]}
            gender={gender}
            onGenderChange={setGender}
          />
        </div>
      </Content>
    </PageRoot>
  );
};
